// PoolScreener data layer — in-memory + localStorage cache, TTL 1h default.
// Screener data (pool universe, candles, funding) changes slowly; caching
// keeps a rescan from hammering the public APIs and staying inside rate
// limits. Memory is the hot path; localStorage survives reloads.
const DEFAULT_TTL_MS = 60 * 60 * 1000; // 1h
const LS_PREFIX = 'poolscreener:cache:';

interface Entry<T> {
  value: T;
  expiresAt: number;
}

const mem = new Map<string, Entry<unknown>>();

function readLs<T>(key: string, now: number): Entry<T> | null {
  try {
    const raw = localStorage.getItem(LS_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Entry<T>;
    if (typeof parsed?.expiresAt !== 'number' || parsed.expiresAt <= now) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeLs<T>(key: string, entry: Entry<T>): void {
  try {
    localStorage.setItem(LS_PREFIX + key, JSON.stringify(entry));
  } catch {
    /* quota / private mode — memory cache still works */
  }
}

/** Return the cached value for `key`, or compute it via `fn`, store, and
 *  return. Memory first, then localStorage, then live. */
export async function cached<T>(
  key: string,
  fn: () => Promise<T>,
  ttlMs: number = DEFAULT_TTL_MS,
): Promise<T> {
  const now = Date.now();

  const m = mem.get(key);
  if (m && m.expiresAt > now) return m.value as T;

  const ls = readLs<T>(key, now);
  if (ls) {
    mem.set(key, ls);
    return ls.value;
  }

  const value = await fn();
  const entry: Entry<T> = { value, expiresAt: now + ttlMs };
  mem.set(key, entry);
  writeLs(key, entry);
  return value;
}

/** Drop everything (both tiers). Used by a manual "rescan" button. */
export function clearCache(): void {
  mem.clear();
  try {
    for (const k of Object.keys(localStorage)) {
      if (k.startsWith(LS_PREFIX)) localStorage.removeItem(k);
    }
  } catch {
    /* ignore */
  }
}
